import React, { Component, useCallback, useEffect, useRef, useState } from 'react';
import { Alert, Box, Button, Dialog, Stack, Typography } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { callsAPI, getApiBaseURL } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import { setCallTabId } from './callTab';
import {
  callAfterGatewayEnded,
  callAfterSignalEnded,
  callStatusLabel,
  isTerminalPhase,
  nextOutboundCall,
} from './callOffer';
import { createRingback } from './ringback';

const CALL_ROLES = ['sales', 'sales_supervisor', 'country_manager', 'sales_admin', 'admin'];
const INBOX_ROLES = ['sales', 'sales_supervisor', 'country_manager', 'admin'];

function mapMic(state) {
  if (state === 'denied') return 'denied';
  if (state === 'granted') return 'granted';
  return 'unknown';
}

function wsUrl() {
  const base = getApiBaseURL();
  if (base) {
    const url = new URL(base);
    url.protocol = url.protocol === 'https:' ? 'wss:' : 'ws:';
    url.pathname = '/api/calls/presence';
    url.search = '';
    return url.toString();
  }
  const proto = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
  return `${proto}//${window.location.host}/api/calls/presence`;
}

function deadlineFrom(signal, receivedAt) {
  const span = new Date(signal.expiresAt).getTime() - new Date(signal.occurredAt).getTime();
  if (!Number.isFinite(span) || span <= 0) return receivedAt + 10000;
  return receivedAt + span;
}

class CallingBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { failed: false };
  }

  static getDerivedStateFromError() {
    return { failed: true };
  }

  render() {
    if (this.state.failed) return null;
    return this.props.children;
  }
}

function CallingSession() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const tabId = useRef(`tab-${Math.random().toString(36).slice(2)}`);
  const socketRef = useRef(null);
  const voiceRef = useRef(null);
  const ringbackRef = useRef(null);
  const pendingStatusRef = useRef(null);
  const callRef = useRef(null);
  const micRef = useRef('unknown');
  const openThreadRef = useRef(() => {});
  const [enabled, setEnabled] = useState(false);
  const [ring, setRing] = useState(null);
  const [call, setCall] = useState(null);
  const [now, setNow] = useState(() => Date.now());
  const [mic, setMic] = useState('unknown');
  const [presenceUp, setPresenceUp] = useState(false);
  const [presenceLate, setPresenceLate] = useState(false);

  function openThread(contactId) {
    if (!contactId || !INBOX_ROLES.includes(user?.role)) return;
    const id = String(contactId);
    const params = new URLSearchParams(window.location.search);
    const onThread = window.location.pathname.replace(/\/$/, '').endsWith('/whatsapp')
      && params.get('contactId') === id;
    if (onThread) return;
    navigate(`/whatsapp?contactId=${encodeURIComponent(id)}`);
  }
  openThreadRef.current = openThread;

  const showCall = useCallback((updater) => {
    const next = typeof updater === 'function' ? updater(callRef.current) : updater;
    callRef.current = next;
    setCall(next);
  }, []);

  const stopRingback = useCallback(() => {
    ringbackRef.current?.stop();
  }, []);

  const stopVoice = useCallback(() => {
    const session = voiceRef.current;
    voiceRef.current = null;
    if (!session) return;
    try { session.ws.send(JSON.stringify({ type: 'leave' })); } catch (_) { /* already closed */ }
    try { session.ws.close(); } catch (_) { /* already closed */ }
    session.pc.getSenders().forEach((sender) => sender.track && sender.track.stop());
    session.pc.close();
    if (session.audio) session.audio.srcObject = null;
  }, []);

  const connectVoice = useCallback(async (voice, callId) => {
    const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    const active = callRef.current;
    if (!active || String(active.callId) !== String(callId) || isTerminalPhase(active.phase)) {
      stream.getTracks().forEach((track) => track.stop());
      return;
    }
    const pc = new RTCPeerConnection({ iceServers: voice.ice_servers || [] });
    stream.getTracks().forEach((track) => pc.addTrack(track, stream));
    const audio = new Audio();
    audio.autoplay = true;
    pc.ontrack = (event) => {
      const [remote] = event.streams;
      if (remote) audio.srcObject = remote;
    };
    const ws = new WebSocket(voice.url);
    const session = { pc, ws, audio };
    voiceRef.current = session;
    pc.onicecandidate = (event) => {
      if (event.candidate && ws.readyState === 1) {
        ws.send(JSON.stringify({ type: 'candidate', candidate: event.candidate }));
      }
    };
    ws.onmessage = async (event) => {
      let msg;
      try { msg = JSON.parse(event.data); } catch (_) { return; }
      if (msg.type === 'ready' || msg === 'ready') {
        const offer = await pc.createOffer();
        await pc.setLocalDescription(offer);
        ws.send(JSON.stringify({ type: 'offer', sdp: offer.sdp }));
      } else if (msg.type === 'answer' && msg.sdp) {
        await pc.setRemoteDescription({ type: 'answer', sdp: msg.sdp });
      } else if (msg.type === 'candidate' && msg.candidate) {
        await pc.addIceCandidate(msg.candidate);
      } else if (msg.type === 'audio_live') {
        stopRingback();
        showCall((current) => (current ? { ...current, liveAt: Date.now(), phase: 'live' } : current));
      } else if (msg.type === 'ended') {
        stopRingback();
        stopVoice();
        showCall((current) => callAfterGatewayEnded(current));
      }
    };
    ws.onopen = () => {
      ws.send(JSON.stringify({ type: 'join', token: voice.token }));
    };
  }, [stopVoice, stopRingback, showCall]);

  useEffect(() => {
    setCallTabId(tabId.current);
    return () => {
      setCallTabId('');
      stopRingback();
    };
  }, [stopRingback]);

  useEffect(() => {
    const role = user?.role;
    if (!role || !CALL_ROLES.includes(role)) return undefined;
    let stop = false;
    callsAPI.status().then((res) => {
      if (!stop) setEnabled(Boolean(res.data?.enabled));
    }).catch(() => {});
    return () => { stop = true; };
  }, [user]);

  useEffect(() => {
    if (!enabled) return undefined;
    const timer = setInterval(() => setNow(Date.now()), 500);
    return () => clearInterval(timer);
  }, [enabled]);

  useEffect(() => {
    if (!enabled) return undefined;
    const timer = setTimeout(() => setPresenceLate(true), 5000);
    return () => clearTimeout(timer);
  }, [enabled]);

  useEffect(() => {
    if (!enabled) return undefined;
    let stop = false;
    let status;
    const apply = (next) => {
      if (stop) return;
      micRef.current = next;
      setMic(next);
    };
    navigator.permissions?.query?.({ name: 'microphone' }).then((result) => {
      status = result;
      apply(mapMic(result.state));
      result.onchange = () => apply(mapMic(result.state));
    }).catch(() => {});
    return () => {
      stop = true;
      if (status) status.onchange = null;
    };
  }, [enabled]);

  useEffect(() => {
    if (!ring) return undefined;
    const left = deadlineFrom(ring, ring.receivedAt) - Date.now();
    const timer = setTimeout(() => {
      setRing((current) => (current && current.callId === ring.callId ? null : current));
    }, Math.max(0, left));
    return () => clearTimeout(timer);
  }, [ring]);

  const canCall = Boolean(user?.role && CALL_ROLES.includes(user.role));

  useEffect(() => {
    if (!canCall) return undefined;
    const onPrime = () => {
      if (!ringbackRef.current) ringbackRef.current = createRingback();
      ringbackRef.current.prime();
    };
    const onVoice = (event) => {
      const detail = event.detail || {};
      if (!detail.voice) return;
      const pending = pendingStatusRef.current;
      let next = {
        callId: detail.callId,
        name: detail.name || 'Call',
        liveAt: null,
        phase: detail.outbound ? 'calling' : 'connecting',
        outbound: Boolean(detail.outbound),
        crmContactId: detail.contactId || null,
      };
      if (detail.outbound && pending && String(pending.callId) === String(detail.callId)) {
        next = nextOutboundCall(next, pending.status);
      }
      callRef.current = next;
      showCall(next);
      if (isTerminalPhase(next.phase)) {
        stopRingback();
        return;
      }
      if (next.phase === 'ringing') {
        if (!ringbackRef.current) ringbackRef.current = createRingback();
        ringbackRef.current.start();
      }
      connectVoice(detail.voice, detail.callId).catch(() => {
        stopRingback();
        const active = callRef.current;
        if (!active || String(active.callId) !== String(detail.callId) || isTerminalPhase(active.phase)) return;
        showCall(null);
        if (detail.callId) callsAPI.hangup(detail.callId).catch(() => {});
      });
    };
    window.addEventListener('bp-call-prime', onPrime);
    window.addEventListener('bp-call-voice', onVoice);
    return () => {
      window.removeEventListener('bp-call-prime', onPrime);
      window.removeEventListener('bp-call-voice', onVoice);
    };
  }, [canCall, connectVoice, stopRingback, showCall]);

  useEffect(() => {
    if (!enabled) return undefined;
    let closed = false;
    let ws;
    const connect = () => {
      ws = new WebSocket(wsUrl());
      socketRef.current = ws;
      ws.onopen = () => {
        ws.send(JSON.stringify({
          type: 'auth',
          token: localStorage.getItem('token'),
          tabId: tabId.current,
          microphone: micRef.current,
        }));
      };
      ws.onmessage = (event) => {
        let msg;
        try { msg = JSON.parse(event.data); } catch (_) { return; }
        if (msg.type === 'ready') {
          setPresenceUp(true);
          ws.send(JSON.stringify({ type: 'heartbeat', microphone: micRef.current }));
        } else if (msg.type === 'ring') {
          const nextRing = { ...msg, receivedAt: Date.now() };
          setRing(nextRing);
          openThreadRef.current(msg.crmContactId);
        } else if (msg.type === 'permission') {
          window.dispatchEvent(new CustomEvent('bp-call-permission', { detail: msg }));
        } else if (msg.type === 'cancel' && !msg.keepCall) {
          setRing((current) => (current && String(current.callId) === String(msg.callId) ? null : current));
        } else if (msg.type === 'outbound_status') {
          pendingStatusRef.current = { callId: msg.callId, status: msg.status };
          const current = callRef.current;
          if (!current || String(current.callId) !== String(msg.callId)) return;
          if (msg.status === 'parent_ringing') {
            if (!ringbackRef.current) ringbackRef.current = createRingback();
            ringbackRef.current.start();
          } else {
            stopRingback();
          }
          if (isTerminalPhase(msg.status)) stopVoice();
          const next = nextOutboundCall(current, msg.status);
          callRef.current = next;
          showCall(next);
        } else if (msg.type === 'ended') {
          stopRingback();
          stopVoice();
          setRing(null);
          showCall((current) => (
            current && (!msg.callId || String(current.callId) === String(msg.callId))
              ? callAfterSignalEnded(current, msg.endReason)
              : current
          ));
        }
      };
      ws.onclose = () => {
        setPresenceUp(false);
        if (!closed) setTimeout(connect, 3000);
      };
    };
    connect();
    const beat = setInterval(() => {
      if (socketRef.current && socketRef.current.readyState === 1) {
        socketRef.current.send(JSON.stringify({ type: 'heartbeat', microphone: micRef.current }));
      }
    }, 10000);
    return () => {
      closed = true;
      clearInterval(beat);
      if (ws) ws.close();
    };
  }, [enabled, stopVoice, stopRingback, showCall]);

  async function accept() {
    if (!ring) return;
    try {
      await navigator.mediaDevices.getUserMedia({ audio: true }).then((stream) => {
        stream.getTracks().forEach((track) => track.stop());
      });
    } catch (_) {
      await callsAPI.decline(ring.callId).catch(() => {});
      setRing(null);
      return;
    }
    const res = await callsAPI.accept(ring.callId, tabId.current);
    if (res.status === 200 && res.data?.voice) {
      const current = ring;
      setRing(null);
      showCall({
        callId: current.callId,
        name: current.displayName,
        liveAt: null,
        phase: 'connecting',
        outbound: false,
        crmContactId: current.crmContactId || null,
      });
      try {
        await connectVoice(res.data.voice, current.callId);
      } catch (_) {
        showCall(null);
        await callsAPI.hangup(current.callId).catch(() => {});
      }
      return;
    }
    setRing(null);
  }

  async function decline() {
    if (!ring) return;
    const id = ring.callId;
    setRing(null);
    await callsAPI.decline(id).catch(() => {});
  }

  function toggleMute() {
    const session = voiceRef.current;
    if (!session) return;
    session.pc.getSenders().forEach((sender) => {
      if (sender.track && sender.track.kind === 'audio') sender.track.enabled = !sender.track.enabled;
    });
  }

  async function hangup() {
    const id = call?.callId;
    pendingStatusRef.current = null;
    stopRingback();
    stopVoice();
    showCall(null);
    if (id) await callsAPI.hangup(id).catch(() => {});
  }

  if (!enabled) return null;
  const remaining = ring ? Math.max(0, deadlineFrom(ring, ring.receivedAt) - now) : 0;
  const terminal = isTerminalPhase(call?.phase);
  const showReachableWarning = mic === 'denied' || (presenceLate && !presenceUp);
  const warningText = mic === 'denied'
    ? 'This browser is blocking the microphone, so you will not be rung for WhatsApp calls.'
    : 'This browser is not connected for WhatsApp calls, so you will not be rung.';

  return (
    <>
      {showReachableWarning && (
        <Alert severity="warning" sx={{ position: 'fixed', top: 72, left: '50%', transform: 'translateX(-50%)', zIndex: 1400, maxWidth: 520 }}>
          {warningText}
        </Alert>
      )}
      <Dialog open={Boolean(ring)} onClose={decline}>
        <Box sx={{ p: 3, minWidth: 280 }}>
          <Typography variant="overline">{ring?.stepLabel}</Typography>
          <Typography variant="h6">{ring?.displayName}</Typography>
          <Typography variant="body2" sx={{ mb: 2 }}>{ring?.leadLabel || ring?.e164}</Typography>
          <Typography variant="body2" sx={{ mb: 2 }}>{Math.ceil(remaining / 1000)}s</Typography>
          <Stack direction="row" spacing={1}>
            <Button variant="contained" color="success" onClick={accept}>Accept</Button>
            <Button variant="outlined" color="error" onClick={decline}>Decline</Button>
          </Stack>
        </Box>
      </Dialog>
      {call && (
        <Box sx={{ position: 'fixed', right: 16, bottom: 16, zIndex: 1400, bgcolor: '#111', color: '#fff', p: 2, borderRadius: 2 }}>
          <Typography variant="body2">{call.name}</Typography>
          <Typography variant="body2">{callStatusLabel(call, now)}</Typography>
          {call.crmContactId && INBOX_ROLES.includes(user?.role) && (
            <Button color="inherit" variant="outlined" onClick={() => openThread(call.crmContactId)} sx={{ mt: 1, mr: 1 }}>
              Open chat
            </Button>
          )}
          {terminal ? (
            <Button color="inherit" variant="outlined" onClick={() => showCall(null)} sx={{ mt: 1 }}>Close</Button>
          ) : (
            <>
              <Button color="inherit" variant="outlined" onClick={toggleMute} sx={{ mt: 1, mr: 1 }}>Mute</Button>
              <Button color="error" variant="contained" onClick={hangup} sx={{ mt: 1 }}>Hang up</Button>
            </>
          )}
        </Box>
      )}
    </>
  );
}

export default function CallingHost() {
  return (
    <CallingBoundary>
      <CallingSession />
    </CallingBoundary>
  );
}
